import _ from 'lodash';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import classNames from 'classnames';

import { Button, Title, Text, Box } from '@nerdwallet/currency';
import ProductTable from '@nerdwallet/react-product-table';
import { getId } from '@nerdwallet/marketplace/selectors/common';
import nds from '@nerdwallet/base-styles/classes';

import CdsFiltersContext from '../../contexts/cds-filters-context/cds-filters-context';
import withAlpsDataDependency from '../../lib/alps/with-alps-data-dependency';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import { getSlug } from '~/server/config-fetcher';

import APYAnnotation from './apy-annotation';
import CTAAnnotation from './cta-annotation';
import DetailsSection from './details-section';
import MonthsInYears from './months-in-years';
import ReviewAnnotation from '../shared-table-components/review-annotation';
import Row from '../row/row';
import LoadingSpinner from '../loading-spinner/loading-spinner';

import createConfig from './config';

import {
  dataDependencyTypePicker,
  stringTextAreaSpec,
  stringInputSpec,
  numberInputSpec,
} from '../../lib/editing-configs';
import getDefaultCtaFromDependency from '../../lib/product-table-utils';

import parseMdlinks from '../../lib/parse-md-links';

import './cds-product-table.less';

const customComponents = {
  APYAnnotation,
  CTAAnnotation,
  MonthsInYears,
  ReviewAnnotation,
};

const filterProducts = (filters, products) => {
  if (!filters) {
    return products;
  }

  let filteredProducts = products;

  if (filters.deposit_minimum) {
    filteredProducts = filteredProducts.filter(
      (product) =>
        parseInt(filters.deposit_minimum, 10) >=
        _.get(product, 'details.minimum_balance.value', 0)
    );
  }

  if (filters.length_of_term) {
    filteredProducts = filteredProducts.filter(
      (product) =>
        parseInt(filters.length_of_term, 10) * 30 >=
        _.get(product, 'details.cd_deposit_term.value', 0)
    );
  }

  if (filters.min_ratings) {
    filteredProducts = filteredProducts.filter(
      (product) =>
        _.get(product, 'product.institution.details.nw_rating.value', 0) >=
        parseInt(filters.min_ratings, 10)
    );
  }

  // Limit to 8 products;
  return filteredProducts.slice(0, 8);
};

const CdsProductTable = ({
  title,
  subtitle,
  ctaCopy,
  data,
  products,
  requestedIds,
}) => {
  const loading = _.get(data, 'loading', false);

  const context = useContext(CdsFiltersContext);
  const { filters } = context;

  const methodologyURL =
    'https://www.nerdwallet.com/blog/banking/ratings-methodology-for-banks-and-credit-unions?trk_app=landing-page-client';
  const methodologyText = `[How we rate banks and credit unions](${methodologyURL})`;
  const clickOutText = 'View all CDs';

  // The new banking-client uses a length_of_term value in days,
  // so we must multiply length_of_term value by 30 to convert months to days
  const clickOutURL = () => {
    const modifiedFilters = { ...filters };
    if (modifiedFilters.length_of_term) {
      modifiedFilters.length_of_term *= 30;
    }
    return `/rates/cds/best-cd-rates?${queryString.stringify(
      modifiedFilters
    )}&trk_app=landing-page-client`;
  };

  if (_.isEmpty(products)) {
    return null;
  }

  const limitedProducts = filterProducts(filters, products);
  /**
   * ALPS provides sorted product ordering by way of requestedIds so we only expect it to exist
   * in the instance we have fetched are using the ALPS data dependency.
   * Products with ALPS entries will be privileged over products that do not have an entry.
   */
  let sortedLimitedProducts = limitedProducts;
  if (requestedIds.length && limitedProducts) {
    sortedLimitedProducts = [...limitedProducts].sort((a, b) => {
      const indexOfA = _.indexOf(requestedIds, getId(a));
      const indexOfB = _.indexOf(requestedIds, getId(b));
      return indexOfA - indexOfB;
    });
  }

  const config = createConfig({
    ctaCopy: ctaCopy || getDefaultCtaFromDependency('cds')(),
  });

  return (
    <Row className="cds-product-table-wrapper" centeredInParent>
      <Row
        className={classNames([
          'cds-product-table__widths',
          {
            [nds('padding-top-4')]: !title,
            [nds('padding-vertical-3')]: title || subtitle,
          },
        ])}
        centeredInParent
      >
        {title && (
          <Title className="cds-product-table__title" size="small">
            {title}
          </Title>
        )}
        {subtitle && (
          <Box className={nds('padding-top-2')}>
            <Text component="p" align="center" size="small">
              {subtitle}
            </Text>
          </Box>
        )}
      </Row>
      <Row
        className={classNames('cds-product-table', 'cds-product-table__widths')}
        centeredInParent
      >
        <ProductTable
          products={sortedLimitedProducts}
          tableName="cds-lp-table"
          tableConfig={config}
          customComponents={customComponents}
          detailsComponent={DetailsSection}
        />
        <Box className="cds-product-table__sub-text">
          <Text size="small">{parseMdlinks(methodologyText)}</Text>
        </Box>
        <Button
          href={clickOutURL()}
          target="_blank"
          className="cds-product-table__button"
        >
          {clickOutText}
        </Button>
        <LoadingSpinner show={loading} />
      </Row>
    </Row>
  );
};

CdsProductTable.defaultProps = {
  filters: {},
  showRating: false,
  data: {},
};

CdsProductTable.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  ctaCopy: PropTypes.string,
  showRating: PropTypes.bool,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  dataDependency: PropTypes.shape({
    type: PropTypes.string,
    filters: PropTypes.shape({
      deposit_minimum: PropTypes.number,
      length_of_term: PropTypes.number,
      min_ratings: PropTypes.string,
    }),
  }),
  // Provided by "withAlpsDataDependency"
  requestedIds: PropTypes.arrayOf(PropTypes.string),
  products: PropTypes.shape(PropTypes.object),
};

CdsProductTable.getEditingConfig = (componentDocument) => {
  const dataDependency = _.get(componentDocument, 'dataDependency.type', '');
  const getDefaultCta = getDefaultCtaFromDependency(dataDependency);

  return [
    stringTextAreaSpec('title', 'Title'),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    stringInputSpec('ctaCopy', 'CTA Button Text', {
      placeholder: getDefaultCta(),
    }),
    dataDependencyTypePicker,
    numberInputSpec(
      'dataDependency.data.filters.deposit_minimum',
      'Default Minimum deposit',
      { isProp: false }
    ),
    numberInputSpec(
      'dataDependency.data.filters.length_of_term',
      'Default Length of Term (months)',
      { isProp: false }
    ),
    stringInputSpec(
      'dataDependency.data.filters.min_ratings',
      'Default Min Nerdwallet Rating (0-5)',
      { isProp: false }
    ),
    stringInputSpec('dataDependency.data.filters.sort_key', 'Sort by', {
      help: 'Valid inputs are apy or terms',
      isProp: false,
    }),
  ];
};

/**
 * Map ingested props to data dependent fields by way of Structured-Content Provider data descriptions.
 * @see {@link https://github.com/NerdWallet/nwjs-structured-content-core#datadependencies}
 *
 * @param {Object} props
 * @param {Object} props.dataDependency - data dependency specifications
 * @param {Object} props.location - location info passed by way of `withRouter` HOC.
 */
/* istanbul ignore next */
const dataDependenciesWithRoute = ({ dataDependency, location }) => {
  const { type } = dataDependency;
  const { pathname = '' } = location;
  if (!type) {
    return null;
  }

  return {
    data: {
      productPage: getSlug(pathname),
      type,
      filters: dataDependency?.data?.filters,
    },
  };
};

export default withAlpsDataDependency(
  CdsProductTable,
  dataDependenciesWithRoute
);
